import { Injectable } from '@angular/core';
import { PpHttpParams, PpHttpService } from '@penpencil/common';
import { User } from 'app/core/user/user.types';
import { environment } from 'environments/environment';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppUrl } from '../constants/app-url';
import { ErrorHandlerService } from '../services/error-handler.service';

@Injectable({
    providedIn: 'root',
})
export class UserService extends ErrorHandlerService {
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);

    /**
     * Constructor
     */
    constructor(
        private _ppHttpService: PpHttpService,
        private appUrl: AppUrl,
    ) {
        super();
        const user = localStorage.getItem('user');
        if (user) {
            this.user = JSON.parse(user);
        }
    }

    get isNeetPgOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.neetPg;
    }

    get isPwOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.pw;
    }

    get isXylemOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.xylem;
    }

    get isCuriousJrOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.curiousJr;
    }

    get isPWTalkOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.talk;
    }

    get isIasOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.ias;
    }

    get isUnigoOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.unigo;
    }

    get isEzpayOrgId(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.ezpay;
    }

    get isInternalOrg(): boolean {
        return (
            this.isPwOrg ||
            this.isNeetPgOrg ||
            this.isIasOrg ||
            this.isUnigoOrg ||
            this.isCuriousJrOrg ||
            this.isEzpayOrgId ||
            this.isMedharthiOrg ||
            this.isYcmouOrg
        );
    }

    get isNursingNextOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.nursingNext;
    }

    get isExternalOrg(): boolean {
        return this.isNursingNextOrg;
    }

    get isPodEM(): boolean {
        const user = this.getUser();
        return user?.id === environment.podEMUserUId;
    }

    get isFinanceUser(): boolean {
        const user = this.getUser();
        return user?.id === environment.financeUserId;
    }

    get isAdmin(): boolean {
        const user = this.getUser();
        return user?.roles.some((role) => role.name === 'Admin');
    }

    get havingFinanceExamEditAccess(): boolean {
        const user = this.getUser();
        return environment.financeExamEditAccessGiveTo?.includes(user?.id);
    }
    get isMedharthiOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.pwmedharthi;
    }

    get isYcmouOrg(): boolean {
        const user = this.getUser();
        return user?.organization?._id === environment.orgIds.ycmou;
    }

    getToken() {
        return localStorage.getItem('pp-token') || null;
    }

    studentList(pagination, query): Observable<any> {
        let skip = 0;
        if (pagination.pageIndex) {
            skip = pagination.pageSize * pagination.pageIndex;
        }
        // let searchParams = {limit: pagination.pageSize, skip: skip.toString()};
        const searchParams = new PpHttpParams();
        searchParams.append('limit', pagination.pageSize);
        searchParams.append('skip', pagination.skip.toString());

        if (query) {
            for (const key in query) {
                if (query.hasOwnProperty(key)) {
                    searchParams.append(key, query[key]);
                }
            }
        }

        searchParams.append('type', 'STUDENT');
        return this._ppHttpService
            .get({ apiPath: this.appUrl.USERS_V2, params: searchParams })
            .pipe(catchError(this.handleError));
    }

    teacherList(pagination, query): Observable<any> {
        let skip = 0;
        if (pagination.pageIndex) {
            skip = pagination.pageSize * pagination.pageIndex;
        }
        const searchParams = {
            type: 'TEACHER',
            response: 'list',
            limit: pagination.pageSize,
            skip,
            page: pagination.pageNo,
        };

        if (query) {
            for (const key in query) {
                if (query.hasOwnProperty(key)) {
                    searchParams[key] = query[key];
                }
            }
        }
        return this._ppHttpService
            .get({
                apiPath: this.appUrl.USERS_V2,
                params: new PpHttpParams().appendAll(searchParams),
            })
            .pipe(catchError(this.handleError));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User) {
        // Store the value
        this._user.next(value);
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current logged in user data
     */
    get(): Observable<User> {
        return of(this.getUser());
    }

    getUser(): User {
        return JSON.parse(localStorage.getItem('user')) ?? {};
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<User> {
        localStorage.setItem('user', JSON.stringify(user));
        return of(user);
    }

    updateUserDetails(data): Observable<any> {
        return this._ppHttpService
            .put(data, {
                apiPath: 'v1/admin/users',
            })
            .pipe(catchError(this.handleError));
    }

    getUserData(): Observable<any> {
        return this._ppHttpService
            .get({
                apiPath: 'v1/admin/users/my-profile',
            })
            .pipe(catchError(this.handleError));
    }

    updateUserPassword(data): Observable<any> {
        return this._ppHttpService
            .post(data, { apiPath: 'v1/admin/users/change-password' })
            .pipe(catchError(this.handleError));
    }
}
